import * as React from 'react';
import { useParams } from 'react-router-dom';

import { App, Button, Card, Modal, Radio, Select, Spin, Typography } from 'antd';

import { useMutation } from '@tanstack/react-query';
import SpinLegacy from 'components/Spin';
import { VERTICAL_URL } from 'settings';
import CopyText from 'v2/components/Shared/CopyText';
import SelectCreditCard from 'v2/components/Shared/SelectCreditCard';
import type { ICard } from 'v2/components/Shared/SelectCreditCard';
import { sendEventToGA } from 'v2/helpers/analytics';
import { numberToBRL } from 'v2/helpers/money';
import useBoolean from 'v2/hooks/useBoolean';
import { useCardsListSubscriptions } from 'v2/hooks/useCardsList';
import { useSubscription } from 'v2/hooks/useSubscription';
import { useSubscriptionChangeProducts } from 'v2/hooks/useSubscriptionChangeProducts';
import type { SubscriptionProductChangePlan } from 'v2/interfaces/shopping';
import { dateService } from 'v2/services/date';
import shoppingSubscriptionService from 'v2/services/shopping/subscription';

import { Icon } from '@eduzz/ui-antd-theme';

const MY_SUBSCRIPTIONS_URL = `${VERTICAL_URL}/subscriptions/me`;

function ChangePlan() {
  const { message, modal } = App.useApp();
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [newProduct, setNewProduct] = React.useState<SubscriptionProductChangePlan>(undefined);
  const [selectedNewProduct, setSelectedNewProduct] = React.useState<SubscriptionProductChangePlan>(undefined);
  const [selectedInstallments, setSelectedInstallments] = React.useState(1);
  const [isSelectCardModalOpen, setIsSelectCardModalOpen] = useBoolean();
  const { subscriptionId } = useParams<{ subscriptionId: string }>();
  const subscriptionQuery = useSubscription(Number(subscriptionId), { enabled: !!subscriptionId });
  const changeProductsQuery = useSubscriptionChangeProducts(Number(subscriptionId), {
    enabled: !!subscriptionQuery.data && isModalOpen,
    retry: false
  });
  const changePlanMutation = useMutation({
    mutationFn: ({
      subscriptionId,
      productId,
      installments
    }: {
      subscriptionId: number;
      productId: number;
      installments: number;
    }) => shoppingSubscriptionService.changeSubscriptionPlan(subscriptionId, productId, installments).toPromise(),
    onError: () => {
      message.error('Falha ao atualizar plano');
    },
    onSuccess: () => {
      message.success('Plano alterado com sucesso!');
      redirectToMySubscriptions();
    }
  });
  const cardsListSubscriptionsQuery = useCardsListSubscriptions({
    enabled: isSelectCardModalOpen
  });
  const changeSubscriptionPaymentMutation = useMutation({
    mutationFn: ({ subscriptionId, card }: { subscriptionId: number; card: ICard }) =>
      shoppingSubscriptionService.changeSubscriptionPayment(subscriptionId, card).toPromise(),
    onSuccess: (_, { card }) => {
      message.success('Forma de pagamento alterada com sucesso!');
      setNewProduct(prevProduct => ({
        ...prevProduct,
        card_number: card.number,
        payment_method_id: card.id,
        payment_method_name: card.name
      }));
    },
    onError: () => {
      message.error('Falha ao alterar forma de pagamento');
      setIsSelectCardModalOpen.toFalse();
    }
  });

  if (subscriptionQuery.isLoading) {
    return (
      <div className='w-full text-center'>
        <SpinLegacy />
      </div>
    );
  }

  if (subscriptionQuery.isError) {
    return (
      <div className='flex w-full flex-col items-center gap-4 py-7.5'>
        Erro ao carregar assinatura.
        <Button type='primary' onClick={() => subscriptionQuery.refetch()}>
          Tentar novamente
        </Button>
      </div>
    );
  }

  const subscription = subscriptionQuery.data;

  function handleOpenModal() {
    setIsModalOpen(true);
  }

  function handleCloseModal() {
    setIsModalOpen(false);
  }

  function handleChangeProduct(product: SubscriptionProductChangePlan) {
    setSelectedNewProduct(product);
  }

  function handleSelectChangeProduct() {
    setIsModalOpen(false);
    setNewProduct(selectedNewProduct);
    setSelectedNewProduct(undefined);
  }

  function handleSelectInstallments(installments: number) {
    setSelectedInstallments(installments);
  }

  function checkIsCreditCard(paymentMethodId: number) {
    return ![1, 20, 25, 31, 32].includes(paymentMethodId);
  }

  function getFrequencyType(product: SubscriptionProductChangePlan) {
    const freqType = product?.frequency_type ?? 'M';
    const frequencyText = {
      M: 'Mensal',
      TM: 'Trimestral',
      SM: 'Semestral',
      Y: 'Anual'
    };

    return frequencyText[freqType as keyof typeof frequencyText];
  }

  function getFrequencyHelper(product: SubscriptionProductChangePlan) {
    const freq = Number(product?.frequency ?? 0);
    const freqType = product?.frequency_type ?? 'M';
    const isPlural = freq > 1;
    const trimester = isPlural ? 'trimestres' : 'trimestre';
    const semester = isPlural ? 'semestres' : 'semestre';
    const year = isPlural ? 'anos' : 'ano';
    const frequencyText = {
      M: `a cada ${freq} ${freq > 1 ? 'meses' : 'mês'}`,
      TM: `a cada ${freq} ${trimester}`,
      SM: `a cada ${freq} ${semester}`,
      Y: `a cada ${freq} ${year}`
    };

    return frequencyText[freqType as keyof typeof frequencyText];
  }

  function shouldShowInstallments(product: SubscriptionProductChangePlan) {
    return (
      checkIsCreditCard(product.payment_method_id) &&
      product.allows_installments &&
      ['SM', 'TM', 'Y'].includes(product.frequency_type)
    );
  }

  if (!subscription) return null;

  function handleConfirm() {
    const dialogMessage = `${
      newProduct.planType === 'upgrade'
        ? 'Atenção! Será realizada uma cobrança com o novo plano na data de hoje para dar início imediato ao novo plano.'
        : `Atenção! A próxima cobrança será realizada apenas ao fim do plano atual, no dia ${dateService.dateToString(
            new Date(newProduct.nextInvoice.due_date),
            'dd/MM/yyyy'
          )}.`
    } Após esta troca de produto a próxima alteração só poderá ser realizada dentro do prazo de 3 meses. Até o término desse período esta ação ficará bloqueada.`;

    modal.confirm({
      title: 'Confirmar alteração de plano',
      content: dialogMessage,
      okText: 'Confirmar',
      cancelText: 'Cancelar',
      cancelButtonProps: { type: 'text' },
      onOk: () => {
        sendEventToGA({
          category: 'Troca de Plano',
          action: 'Botão',
          label: 'Confirmar'
        });

        changePlanMutation.mutate({
          subscriptionId: subscription.id,
          productId: newProduct.id,
          installments: checkIsCreditCard(newProduct.payment_method_id) ? selectedInstallments : 1
        });
      }
    });
  }

  function handleSelectCard(card: ICard) {
    changeSubscriptionPaymentMutation.mutate({
      subscriptionId: subscription.id,
      card
    });
    setIsSelectCardModalOpen.toFalse();
  }

  function redirectToMySubscriptions() {
    window.location.replace(MY_SUBSCRIPTIONS_URL);
  }

  return (
    <>
      <Modal
        title='Selecionar produto'
        open={isModalOpen}
        onCancel={handleCloseModal}
        footer={
          <div className='flex flex-wrap items-center justify-end gap-2'>
            <Button type='text' onClick={handleCloseModal}>
              Cancelar
            </Button>
            <Button
              type='primary'
              disabled={changeProductsQuery.isLoading || !selectedNewProduct}
              onClick={handleSelectChangeProduct}
            >
              Selecionar
            </Button>
          </div>
        }
      >
        {changeProductsQuery.isLoading && (
          <div className='text-center'>
            <Spin />
          </div>
        )}
        {!changeProductsQuery.isLoading &&
          (changeProductsQuery.data ? (
            <Radio.Group
              name='changeProduct'
              value={selectedNewProduct ? String(selectedNewProduct.id) : ''}
              onChange={e => {
                const product = changeProductsQuery.data.find(p => String(p.id) === e.target.value);
                handleChangeProduct(product);
              }}
              className='flex flex-col gap-4'
            >
              {changeProductsQuery.data.map(product => (
                <Radio key={product.id} value={String(product.id)}>
                  {product.id} - {product.title}
                </Radio>
              ))}
            </Radio.Group>
          ) : (
            <span className='mb-1 text-xs'>Não existe nenhum produto disponível para trocar o plano.</span>
          ))}
      </Modal>

      <h1 className='mt-2 text-xl font-semibold'>Altere seu plano</h1>
      <p className='text-sm'>
        Troque seu plano de forma rápida e simples. Através desta tela poderá fazer o upgrade ou downgrade da sua
        assinatura já adquirida. Selecione o novo plano, confirme o método de pagamento, e pronto! 😉
      </p>
      <div className='flex flex-col gap-4'>
        <Card className='mb-4'>
          <span className='mb-1 block text-xs'>
            <strong className='font-semibold'>Produto atual:</strong>{' '}
            <CopyText value={subscription.product.id}>{subscription.product.id}</CopyText> -{' '}
            {subscription.product.title}
          </span>
          <span className='block text-xs'>
            <strong className='font-semibold'>Valor:</strong> {numberToBRL(subscription.total_price)}
          </span>
        </Card>
        <Card className='mb-4'>
          <Button icon={<Icon.Search size={24} />} onClick={handleOpenModal}>
            Selecionar novo produto
          </Button>
          {!!newProduct && (
            <>
              <div className='h-6' />
              <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                <div>
                  <span className='mb-1 block text-xs'>
                    <strong className='font-semibold'>Novo produto:</strong>{' '}
                    <CopyText value={newProduct.id}>{newProduct.id}</CopyText> - {newProduct.title}
                  </span>
                  <span className='block text-xs'>
                    <strong className='font-semibold'>Valor:</strong> {numberToBRL(newProduct.value)}
                  </span>
                </div>
                <div>
                  <span className='mb-1 block text-xs'>
                    <strong className='font-semibold'>Tipo de frequência:</strong> {getFrequencyType(newProduct)}
                  </span>
                  <span className='block text-xs'>
                    <strong className='font-semibold'>Frequência:</strong> {getFrequencyHelper(newProduct)}
                  </span>
                </div>
              </div>
            </>
          )}
        </Card>
        {!!newProduct && (
          <>
            <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
              <Card className='mb-4'>
                <span className='mb-2 block text-xs font-bold'>Fatura anterior:</span>
                <span className='block text-xs'>
                  <strong className='font-semibold'>Valor:</strong> {numberToBRL(newProduct.lastInvoice.value)}
                </span>
                <span className='block text-xs'>
                  <strong className='font-semibold'>Data da emissão:</strong>{' '}
                  {dateService.dateToString(new Date(newProduct.lastInvoice.creation_date), 'dd/MM/yyyy')}
                </span>
                <span className='mb-4 block text-xs'>
                  <strong className='font-semibold'>Vencimento:</strong>{' '}
                  {dateService.dateToString(new Date(newProduct.lastInvoice.due_date), 'dd/MM/yyyy')}
                </span>
                <span className='mb-2 block text-xs font-bold'>Fatura atual:</span>
                <span className='block text-xs'>
                  <strong className='font-semibold'>Valor:</strong> {numberToBRL(newProduct.nextInvoice.value)}
                </span>
                <span className='block text-xs'>
                  <strong className='font-semibold'>Data da emissão:</strong>{' '}
                  {dateService.dateToString(new Date(newProduct.nextInvoice.creation_date), 'dd/MM/yyyy')}
                </span>
                <span className='block text-xs'>
                  <strong className='font-semibold'>Vencimento:</strong>{' '}
                  {dateService.dateToString(new Date(newProduct.nextInvoice.due_date), 'dd/MM/yyyy')}
                </span>
              </Card>
              <Card className='mb-4 h-full'>
                <span className='mb-2 block text-xs font-bold'>Método de pagamento</span>
                <span className='block text-xs'>{newProduct.payment_method_name}</span>
                {checkIsCreditCard(newProduct.payment_method_id) && (
                  <span className='block text-xs'>
                    Cartão de crédito terminado em {newProduct.card_number}{' '}
                    <Typography.Link onClick={setIsSelectCardModalOpen.toTrue}>Alterar cartão</Typography.Link>
                  </span>
                )}
                {shouldShowInstallments(newProduct) && (
                  <div className='mt-4'>
                    <label className='mb-1 block text-xs'>Número de parcelas</label>
                    <Select
                      value={selectedInstallments}
                      onChange={value => handleSelectInstallments(Number(value))}
                      id='change-plan-installments-select'
                      className='w-full'
                      options={Array(newProduct.nextInvoice.max_installments)
                        .fill(1)
                        .map((value, index) => ({
                          value: value + index,
                          label: `${value + index} ${value + index > 1 ? 'vezes' : 'vez'}`
                        }))}
                    />
                  </div>
                )}
              </Card>
            </div>
            <div className='mt-6 flex flex-wrap items-center justify-end gap-2'>
              <Button type='text' onClick={redirectToMySubscriptions}>
                Cancelar
              </Button>
              <Button
                type='primary'
                disabled={changePlanMutation.isPending || changeSubscriptionPaymentMutation.isPending}
                onClick={handleConfirm}
                id='change-plan-confirm-button'
                loading={changePlanMutation.isPending || changeSubscriptionPaymentMutation.isPending}
              >
                Confirmar
              </Button>
            </div>
            <SelectCreditCard
              isOpen={isSelectCardModalOpen}
              onClose={setIsSelectCardModalOpen.toFalse}
              onFinish={handleSelectCard}
              cardsList={cardsListSubscriptionsQuery.data}
              isLoadingCards={cardsListSubscriptionsQuery.isLoading}
            />
          </>
        )}
      </div>
    </>
  );
}

export { ChangePlan };
